<template>
  <div>
    <b-alert
      v-if="restoredDraftAt"
      show
      variant="info"
      class="p-1"
    >
      {{ $t('partner.order-request.autosave.restored', { date: formatDate(restoredDraftAt) }) }}
    </b-alert>

    <b-alert
      v-if="apiError"
      show
      variant="danger"
      class="p-1"
    >
      {{ apiError }}
    </b-alert>

    <partner-order-request-form
      v-model="form"
      :allowed-customers="allowedCustomers"
      :allowed-order-types="allowedOrderTypes"
      :order-request-instruction="partnerOrderRequestInstruction"
      :loading="loading || submitting"
    />

    <div class="d-flex flex-wrap gap-1 mt-1">
      <b-button
        variant="primary"
        :disabled="loading || submitting"
        @click="submitCreate"
      >
        {{ $t('partner.order-request.actions.create') }}
      </b-button>
      <b-button
        variant="outline-secondary"
        :disabled="loading || submitting"
        @click="clearAutosave"
      >
        {{ $t('partner.order-request.actions.clear-draft') }}
      </b-button>
      <b-button
        variant="flat-secondary"
        :to="{ name: 'partner-order-request-list' }"
      >
        {{ $t('partner.order-request.actions.back-to-list') }}
      </b-button>
    </div>
  </div>
</template>

<script>
import { BAlert, BButton } from 'bootstrap-vue'
import { v4 as uuidv4 } from 'uuid'
import { parseRequestError, transformDatetimeToHumanReadablePretty } from '@/helpers/helpers'
import orderEventKinds from '@/dictionaries/orderEventKinds'
import orderTypes from '@/dictionaries/orderTypes'
import PartnerOrderRequestForm from './components/PartnerOrderRequestForm.vue'
import {
  buildOrderRequestPayloadFromForm,
  createEmptyOrderRequestForm,
  validateOrderRequestPayload,
} from './helpers/formModel'
import {
  clearOrderRequestDraft,
  getCreateDraftStorageKey,
  readOrderRequestDraft,
  saveOrderRequestDraft,
} from './helpers/draftStorage'

export default {
  name: 'PartnerOrderRequestCreate',
  components: {
    BAlert,
    BButton,
    PartnerOrderRequestForm,
  },
  data() {
    return {
      loading: false,
      submitting: false,
      form: createEmptyOrderRequestForm(),
      idempotencyKey: uuidv4(),
      restoredDraftAt: null,
      apiError: null,
    }
  },
  computed: {
    allowedCustomers() {
      return this.$store.getters['externalOrderRequest/getAllowedCustomers']
    },
    allowedOrderTypes() {
      return this.$store.getters['externalOrderRequest/getAllowedOrderTypes']
    },
    partnerOrderRequestInstruction() {
      return this.$store.getters['externalOrderRequest/getPartnerOrderRequestInstruction']
    },
    createDraftStorageKey() {
      return getCreateDraftStorageKey()
    },
  },
  watch: {
    form: {
      deep: true,
      handler() {
        saveOrderRequestDraft(this.createDraftStorageKey, this.form)
      },
    },
  },
  created() {
    this.initializePage()
  },
  methods: {
    initializePage() {
      this.loading = true
      this.restoreDraftIfExists()

      this.$store.dispatch('externalOrderRequest/fetchAllowedCustomers')
        .then(() => {
          this.applyDefaultCustomerIfMissing()
          this.applyDefaultRequestedTypeIfMissing()
        })
        .catch(error => {
          parseRequestError(error)
        })
        .finally(() => {
          this.loading = false
        })
    },
    restoreDraftIfExists() {
      const draft = readOrderRequestDraft(this.createDraftStorageKey)
      if (!draft?.payload) {
        return
      }

      this.form = {
        ...createEmptyOrderRequestForm(),
        ...draft.payload,
      }
      this.restoredDraftAt = draft.savedAt || null
    },
    applyDefaultCustomerIfMissing() {
      if (this.form.customerId) {
        return
      }

      const [firstCustomer] = this.allowedCustomers
      if (!firstCustomer?.id) {
        return
      }

      this.form = {
        ...this.form,
        customerId: firstCustomer.id,
      }
    },
    applyDefaultRequestedTypeIfMissing() {
      if (this.form.requestedType) {
        return
      }

      const [firstType] = this.allowedOrderTypes
      const defaultType = this.resolveOrderTypeValue(firstType)

      if (!defaultType) {
        return
      }

      this.form = {
        ...this.form,
        requestedType: defaultType,
        eventKind: defaultType === orderTypes.event
          ? (this.form.eventKind || orderEventKinds.medical_coverage)
          : null,
      }
    },
    resolveOrderTypeValue(type) {
      if (typeof type === 'string') {
        return type
      }

      if (type && typeof type === 'object' && typeof type.value === 'string') {
        return type.value
      }

      return ''
    },
    clearAutosave() {
      clearOrderRequestDraft(this.createDraftStorageKey)
      this.form = createEmptyOrderRequestForm()
      this.applyDefaultCustomerIfMissing()
      this.applyDefaultRequestedTypeIfMissing()
      this.restoredDraftAt = null
      window.toast.notify.success(this.$t('partner.order-request.autosave.cleared'))
    },
    async submitCreate() {
      if (this.submitting) {
        return
      }

      this.submitting = true
      this.apiError = null

      try {
        const payload = buildOrderRequestPayloadFromForm(this.form)
        const missingFields = validateOrderRequestPayload(payload)
        if (missingFields.length > 0) {
          this.apiError = this.$t('partner.order-request.errors.required-fields')
          return
        }

        payload.idempotencyKey = this.idempotencyKey

        const response = await this.$store.dispatch('externalOrderRequest/createPartnerOrderRequest', payload)
        const requestId = response?.data?.id
        clearOrderRequestDraft(this.createDraftStorageKey)

        if (requestId) {
          this.$router.push({
            name: 'partner-order-request-details',
            params: { id: requestId },
          })
        } else {
          this.$router.push({ name: 'partner-order-request-list' })
        }
      } catch (error) {
        parseRequestError(error)
        this.apiError = error?.response?.data?.message || this.$t('partner.order-request.errors.create')
      } finally {
        this.submitting = false
      }
    },
    formatDate(value) {
      return transformDatetimeToHumanReadablePretty(value)
    },
  },
}
</script>

<style lang="scss" scoped>
.gap-1 {
  gap: 0.5rem;
}
</style>
